import { Capacitor } from '@capacitor/core'
import {
  AppBar,
  Box,
  Button,
  Skeleton,
  Stack,
  Toolbar,
  Typography,
} from '@mui/material'
import { useCallback, useEffect, useState } from 'react'
import { IconButton, Icons, SkeletonText } from 'utoniq-ui'
import { InformationModal } from 'utoniq-ui'

import {
  ArtistMembershipQuery,
  ArtistWithSubscriptionFragment,
  EscottMasterInput,
  Maybe,
  MembershipInterval,
  MembershipSubscriptionPlanDetailFragment,
  MembershipSubscriptionPlanStatus,
  MembershipSubscriptionProductDetailFragment,
  PatronType,
  PaymentMethod,
  SubscriptionDetailFragment,
} from '@/clients/utoniq-core/schema'
import { BaseLayout, Head, HeaderImageArea } from '@/components/layouts'
import { ProductBox, RequiredMembershipInfoBox } from '@/components/organisms'
import {
  ExpiredCardModal,
  PaymentErrorModal,
  Require3DSecureModal,
} from '@/components/organisms/functionals/PaymentErrorModal'
import {
  LegalInformationLinksCell,
  PurchaseCautionText,
  SpecificCommercialTransactionLawLink,
  WageSettlementMethodLink,
} from '@/components/organisms/legalInformation'
import {
  ArtistThemeProvider,
  styles,
  useArtistThemeContext,
} from '@/components/styles'
import { config, constants } from '@/config'
import { useMessage, useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { captureError } from '@/core/errors'
import { individualDisplayResponsive } from '@/core/individual'
import { MembershipSubscriptionProduct } from '@/core/membership'
import { isCarrierPaymentMethod } from '@/lib/escott'
import { usePrivateAction } from '@/lib/privateAction'

export * from './fragments'

export type State = {
  loading: boolean
  subscribing: boolean
  isLoggedIn: boolean
  isMember: boolean
  fetchingDiffAmountSubscription: boolean
  appName?: string
  stopSubscription: boolean
  isPersonalInformationRequired: boolean
  isMembershipPersonalInformationRequired: boolean
  requireRegisteredCredit: boolean
  artist: Maybe<ArtistMembershipQuery['artist']>
  products: Maybe<MembershipSubscriptionProductDetailFragment[]>
  pendingPurchasePatron: Maybe<
    ArtistWithSubscriptionFragment['pendingPurchasePatron']
  >
  activeProduct?: MembershipSubscriptionProduct
  downgradeProduct?: MembershipSubscriptionProduct
  upgradeProduct?: MembershipSubscriptionProduct
  subscription: Maybe<SubscriptionDetailFragment>
  expiredAtSubscription: Maybe<string>
  diffAmountSubscription: number
  downgradeAtNextPeriod: boolean
  paymentMethod: Maybe<PaymentMethod>
  patronType: Maybe<PatronType>
  store: Maybe<string>
  enableMembershipCarrierPayment: boolean
  selectFavoriteMemberRequired: boolean
}

export type Action = {
  onSubscribePlan: (planId: string) => Promise<void>
  onDowngradePlan: (
    planId: string,
    paymentMethod?: PaymentMethod,
    escottInput?: EscottMasterInput
  ) => Promise<void>
  onUpgradePlan: (
    planId: string,
    paymentMethod?: PaymentMethod,
    escottInput?: EscottMasterInput
  ) => Promise<void>
  onPreviewSubscriptionUpdate: (planId: string) => Promise<void>
  onStopSubscription: () => Promise<void>
  onCancelChangePlan: (
    paymentMethod?: PaymentMethod,
    escottInput?: EscottMasterInput
  ) => Promise<void>
  onClearSelectFavoriteMember: () => void
}

export const ArtistMembershipTemplate: Template<State, Action> = ({
  state,
  onSubscribePlan,
  onDowngradePlan,
  onUpgradePlan,
  onPreviewSubscriptionUpdate,
  onStopSubscription,
  onCancelChangePlan,
  onClearSelectFavoriteMember,
}) => {
  const { t } = useTranslation('artistMembership')
  const { router, routerNames, generatePath, routerPages } = useRouter<{
    artistId: string
    process?: string
    status?: string
  }>()
  const defaultBackPage = generatePath(routerPages.ArtistTop, {
    artistId: state.artist?.id ?? '#',
  })
  const { handleLoginModal } = usePrivateAction()
  const { showMessage, showError } = useMessage()
  const [currentSelectPlan, setCurrentSelectPlan] =
    useState<MembershipSubscriptionPlanDetailFragment>()
  const [openConfirm, setOpenConfirm] = useState(false)
  const [openDowngradeConfirm, setOpenDowngradeConfirm] = useState(false)
  const [openUpgradeConfirm, setOpenUpgradeConfirm] = useState(false)
  const [openStopConfirm, setOpenStopConfirm] = useState(false)
  const [openCancelChangePlanConfirm, setOpenCancelChangePlanConfirm] =
    useState(false)
  const [openCarrierUpgradeConfirm, setOpenCarrierUpgradeConfirm] =
    useState(false)
  const [openKonbiniUpgradeConfirm, setOpenKonbiniUpgradeConfirm] =
    useState(false)
  const [openExpiredCardModal, setOpenExpiredCardModal] = useState(false)
  const [openPaymentErrorModal, setOpenPaymentErrorModal] = useState(false)
  const [openRequire3DSecureModal, setOpenRequire3DSecureModal] =
    useState(false)

  const handleSubscribePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId) return
    await onSubscribePlan(currentSelectPlan.planId)
      .then(() => showMessage(t('completeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(e)
      })
    setOpenConfirm(false)
  }, [currentSelectPlan?.planId, onSubscribePlan, showError, showMessage, t])

  const handleDowngradePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId || !state.paymentMethod) return
    await onDowngradePlan(currentSelectPlan.planId, state.paymentMethod, {
      // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
      successUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=downgrade`,
      // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
      cancelUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=downgrade`,
      // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
      errorUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=downgrade`,
    })
      .then(() => {
        // キャリア決済の場合は外部キャリア決済画面にて
        // 追加手続きが必要なためトーストメッセージを表示しない
        if (
          !!state.paymentMethod &&
          isCarrierPaymentMethod(state.paymentMethod)
        )
          return
        showMessage(t('completeDowngradeSubscribe'))
      })
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(e)
      })
    // mutation の完了から外部キャリア決済画面に遷移するまでの間に
    // 画面内で操作が行われてしまうことを防ぐため
    // キャリア決済の場合はモーダルを閉じない
    if (!isCarrierPaymentMethod(state.paymentMethod))
      setOpenDowngradeConfirm(false)
  }, [
    currentSelectPlan?.planId,
    state.paymentMethod,
    router.query.artistId,
    onDowngradePlan,
    showError,
    showMessage,
    t,
  ])

  const handleUpgradePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId) return
    await onUpgradePlan(currentSelectPlan.planId)
      .then(() => showMessage(t('completeUpgradeSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        switch (e.graphQLErrors?.[0]?.extensions?.errorCode) {
          /**
           * @description
           * 3Dセキュア認証が必要な場合
           */
          case constants.errorCodes.REQUIRE_3D_SECURE:
            setOpenRequire3DSecureModal(true)
            break
          /**
           * @description
           * カードが有効期限切れの場合
           */
          case constants.errorCodes.EXPIRED_CARD:
            setOpenExpiredCardModal(true)
            break
          /**
           * @description
           * 何かしらの決済エラーの場合
           */
          case constants.errorCodes.PAYMENT_ERROR:
            setOpenPaymentErrorModal(true)
            break
          default:
            showError(e)
            break
        }
      })
    setOpenUpgradeConfirm(false)
  }, [currentSelectPlan?.planId, onUpgradePlan, showError, showMessage, t])

  const handleStopSubscription = useCallback(async () => {
    await onStopSubscription()
      .then(() => showMessage(t('completeStopSubscribe')))
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(new Error(t('error')))
      })
    setOpenStopConfirm(false)
  }, [onStopSubscription, showError, showMessage, t])

  const handleCancelChangePlan = useCallback(async () => {
    if (!state.paymentMethod) return
    await onCancelChangePlan(state.paymentMethod, {
      // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
      successUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=changePlanCancel`,
      // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
      cancelUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=changePlanCancel`,
      // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
      errorUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=changePlanCancel`,
    })
      .then(() => {
        // キャリア決済の場合は外部キャリア決済画面にて
        // 追加手続きが必要なためトーストメッセージを表示しない
        if (
          !!state.paymentMethod &&
          isCarrierPaymentMethod(state.paymentMethod)
        )
          return
        showMessage(t('completeCancelChangePlanSubscribe'))
      })
      .catch(e => {
        captureError(e, 'ArtistMembership')
        showError(new Error(t('error')))
      })
    // mutation の完了から外部キャリア決済画面に遷移するまでの間に
    // 画面内で操作が行われてしまうことを防ぐため
    // キャリア決済の場合はモーダルを閉じない
    if (!state.paymentMethod || !isCarrierPaymentMethod(state.paymentMethod))
      setOpenCancelChangePlanConfirm(false)
  }, [
    onCancelChangePlan,
    state.paymentMethod,
    router.query.artistId,
    showError,
    showMessage,
    t,
  ])

  const handleCarrierUpgradePlan = useCallback(async () => {
    if (!currentSelectPlan?.planId || !state.paymentMethod) return
    await onUpgradePlan(currentSelectPlan.planId, state.paymentMethod, {
      // 外部キャリア決済画面での処理が成功した場合のリダイレクト先
      successUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=upgrade`,
      // 外部キャリア決済画面での処理がキャンセルされた場合のリダイレクト先
      cancelUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=upgrade`,
      // 外部キャリア決済画面での処理がエラーになった場合のリダイレクト先
      errorUrl: `${window.location.origin}${generatePath(
        routerPages.ArtistMembership,
        {
          artistId: router.query.artistId,
        }
      )}?process=upgrade`,
    }).catch(e => {
      captureError(e, 'CarrierUpgradePlan')
      showError(new Error(t('error')))
    })
  }, [
    currentSelectPlan?.planId,
    state.paymentMethod,
    router.query.artistId,
    onUpgradePlan,
    showError,
    showMessage,
    t,
  ])

  const isNative = Capacitor.isNativePlatform()

  const displayResponsive = individualDisplayResponsive()

  // プランのアップグレード実行時
  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    // プランのアップグレード実行時以外は何もしない
    if (router.query.process !== 'upgrade') return

    switch (router.query.status) {
      case 'success':
        showMessage(t('carrierUpgradeSuccess'))
        break
      case 'cancel':
        showMessage(t('carrierUpgradeCancel'))
        break
      case 'error':
        showError(t('carrierUpgradeError'))
        break
      default:
        break
    }
  }, [router.query.process, router.query.status])

  // プランのダウングレード実行時
  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    // プランのダウングレード実行時以外は何もしない
    if (router.query.process !== 'downgrade') return

    switch (router.query.status) {
      case 'success':
        showMessage(t('completeDowngradeSubscribe'))
        break
      case 'cancel':
        showMessage(t('cancelDowngradeSubscribe'))
        break
      case 'error':
        showError(t('error'))
        break
      default:
        break
    }
  }, [router.query.process, router.query.status])

  // プラン変更キャンセル実行時
  // 外部キャリア決済画面からのリダイレクトで戻ってきた際
  // トーストメッセージを表示する処理
  useEffect(() => {
    // プラン変更キャンセル実行時以外は何もしない
    if (router.query.process !== 'changePlanCancel') return

    switch (router.query.status) {
      case 'success':
        showMessage(t('completeCancelChangePlanSubscribe'))
        break
      case 'cancel':
        showMessage(t('cancelChangePlanSubscribe'))
        break
      case 'error':
        showError(t('error'))
        break
      default:
        break
    }
  }, [router.query.process, router.query.status])

  return (
    <ArtistThemeProvider>
      <Head title={[routerNames.ArtistMembership, state.artist?.displayName]} />

      <BaseLayout
        safeArea={{ pt: 0 }}
        maxWidth={displayResponsive ? 1120 : undefined}
      >
        {state.loading ? (
          <ArtistMemberLoadingCell
            onClose={() => router.goBack(defaultBackPage)}
          />
        ) : (
          <Box
            {...(displayResponsive
              ? { width: '100%', alignItems: 'center' }
              : {})}
          >
            <BannerCell
              artist={state.artist}
              onClose={() => router.goBack(defaultBackPage)}
              responsive={displayResponsive}
            />
            <Box
              sx={{
                mt: state.appName ? '-48px' : '-60px',
                px: 5,
                position: 'relative',
                zIndex: 1,
                ...(displayResponsive ? { maxWidth: 600, mx: 'auto' } : {}),
              }}
            >
              <Typography variant="h2" sx={{ py: 3 }} align="center">
                {/* ろこまこあこアプリの場合、表示名をアプリ名に */}
                {config.applicationKey === 'rocomacoaco'
                  ? config.appName
                  : state.artist?.displayName}
              </Typography>
              <Typography variant="body1">
                {t('memberDesc', {
                  displayName: state.artist?.displayName ?? '',
                })}
              </Typography>
            </Box>
            {state.isPersonalInformationRequired &&
              state.isMembershipPersonalInformationRequired && (
                <Box
                  px={5}
                  pt={5}
                  pb={1}
                  {...(displayResponsive ? { maxWidth: 600, mx: 'auto' } : {})}
                >
                  <RequiredMembershipInfoBox visibleLink />
                </Box>
              )}
            {!state.appName && (
              <Stack
                sx={{ px: 5 }}
                {...(displayResponsive ? { maxWidth: 600, mx: 'auto' } : {})}
              >
                <Stack spacing={5} sx={{ pt: 4, pb: 6 }}>
                  {state.products
                    ?.filter(product => {
                      // interval: month のプランが存在し、かつそれがactiveでない場合は除外
                      // yearプランのみの場合は表示しない
                      const monthPlan = product.plans.find(
                        plan => plan.interval === MembershipInterval.Month
                      )
                      if (
                        monthPlan &&
                        monthPlan.status !==
                          MembershipSubscriptionPlanStatus.Active
                      ) {
                        return false
                      }
                      return true
                    })
                    .filter(product => product.plans.length > 0)
                    .map(product => (
                      <ProductBox
                        key={product.productId}
                        product={product}
                        expiredAt={state.expiredAtSubscription}
                        activeProduct={state.activeProduct}
                        downgradeProduct={state.downgradeProduct}
                        loading={
                          product.plans.some(
                            plan => currentSelectPlan?.planId === plan.planId
                          ) && state.fetchingDiffAmountSubscription
                        }
                        pendingPurchasePatron={state.pendingPurchasePatron}
                        // プランごとに表示されるボタンを押したときの挙動
                        onClickButton={(event, plan) => {
                          handleLoginModal(async () => {
                            // テンプレートの状態に「現在選択しているプラン」をセット
                            setCurrentSelectPlan(plan)
                            // 選択済み推しメンバー情報を削除する
                            onClearSelectFavoriteMember()

                            /**
                             * ネイティブの場合は複数プランが作れないので、サブスク購入モーダルを開いてreturn
                             */
                            if (isNative) {
                              setOpenConfirm(true)
                              return
                            }
                            // プランのステータス毎のモーダルの表示
                            switch (plan?.planRelationship) {
                              // ダウングレードのキャンセル
                              case 'forLeave':
                              case 'forJoinedUpper':
                              case 'forJoinedLower':
                              case 'carrier_forJoinedLower':
                                return setOpenCancelChangePlanConfirm(true)
                              // アップグレードのキャンセル
                              case 'carrier_forJoinedUpper':
                                return setOpenCancelChangePlanConfirm(true)
                              // ダウングレード
                              case 'lower':
                                return setOpenDowngradeConfirm(true)
                              // キャリア決済アップグレード
                              // キャリア決済利用中かつ下位プランありの場合
                              // その下位プランに用いる
                              case 'carrier_lower':
                                return setOpenDowngradeConfirm(true)
                              // アップグレード
                              case 'upper':
                                await onPreviewSubscriptionUpdate(plan.planId)
                                return setOpenUpgradeConfirm(true)
                              // キャリア決済アップグレード
                              // キャリア決済利用中かつ上位プランありの場合
                              // その上位プランに用いる
                              case 'carrier_upper':
                                return setOpenCarrierUpgradeConfirm(true)
                              // アップグレード
                              case 'konbini_upper':
                                return setOpenKonbiniUpgradeConfirm(true)
                              // ダウングレード
                              case 'konbini_lower':
                                return router.push(
                                  generatePath(
                                    routerPages.ArtistMembershipPaymentEdit,
                                    {
                                      artistId: state.artist?.id ?? '#',
                                      productId: product.productId ?? '#',
                                    }
                                  )
                                )
                              // サブスクの停止
                              case 'joined':
                                return setOpenStopConfirm(true)
                              // サブスクを開始
                              case 'unjoined':
                                if (state.selectFavoriteMemberRequired) {
                                  return router.push(
                                    generatePath(
                                      routerPages.ArtistMembershipFavoriteMembersSelect,
                                      {
                                        artistId: state.artist?.id,
                                        productId:
                                          product.productId?.toString(),
                                      }
                                    )
                                  )
                                }
                                return router.push(
                                  generatePath(
                                    routerPages.ArtistMembershipPurchaseProceed,
                                    {
                                      artistId: state.artist?.id,
                                      productId: product.productId?.toString(),
                                    }
                                  )
                                )
                              default:
                                return router.push(
                                  generatePath(
                                    routerPages.ArtistMembershipPurchaseProceed,
                                    {
                                      artistId: state.artist?.id,
                                      productId: product.productId?.toString(),
                                    }
                                  )
                                )
                            }
                          })(event as never)
                        }}
                        patronType={state.patronType}
                        store={state.store}
                        enableMembershipCarrierPayment={
                          state.enableMembershipCarrierPayment
                        }
                      />
                    ))}
                </Stack>
              </Stack>
            )}
            {!!state.appName && (
              <>
                <WebInductionCell appName={state.appName} />
              </>
            )}
            <LegalInformationLinksCell
              sx={{ px: 5, maxWidth: 600, mx: 'auto' }}
            />
          </Box>
        )}
        <InformationModal
          open={openConfirm}
          onClose={() => setOpenConfirm(false)}
          title={t('confirm')}
          content={
            <>
              <Typography variant="body2">
                {t('confirmContent', {
                  price: currentSelectPlan?.amount ?? 0,
                })}
              </Typography>
              <Stack sx={{ py: 6, gap: 2 }}>
                <PurchaseCautionText />
                <WageSettlementMethodLink variant="caption" />
                <SpecificCommercialTransactionLawLink variant="caption" />
              </Stack>
            </>
          }
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleSubscribePlan}
              loading={state.subscribing}
            >
              {t('becomeMember')}
            </Button>
          }
        />
        <InformationModal
          open={openDowngradeConfirm}
          onClose={() => setOpenDowngradeConfirm(false)}
          title={t('downgradeConfirm')}
          content={
            <Stack spacing={3} sx={{ pb: 7 }}>
              <Typography variant="body2">
                {t('downgradeConfirmContent')}
              </Typography>
              <Typography variant="body2">
                {t('downgradeConfirmExpired', {
                  expiredAt: DateTime.formatDate(state.expiredAtSubscription),
                })}
              </Typography>
              <Stack sx={{ pt: 6, gap: 2 }}>
                <PurchaseCautionText />
                <WageSettlementMethodLink variant="caption" />
                <SpecificCommercialTransactionLawLink variant="caption" />
              </Stack>
            </Stack>
          }
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleDowngradePlan}
              loading={state.subscribing}
            >
              {t('downgrade')}
            </Button>
          }
        />
        <InformationModal
          open={openUpgradeConfirm}
          onClose={() => setOpenUpgradeConfirm(false)}
          title={t('upgradeConfirm')}
          content={
            <Stack spacing={3} sx={{ pb: 7 }}>
              <Typography variant="body2">
                {t('upgradeConfirmContent', {
                  diff: state.diffAmountSubscription,
                })}
              </Typography>
              <Stack sx={{ pt: 6, gap: 2 }}>
                <PurchaseCautionText />
                <WageSettlementMethodLink variant="caption" />
                <SpecificCommercialTransactionLawLink variant="caption" />
              </Stack>
            </Stack>
          }
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleUpgradePlan}
              loading={state.subscribing}
            >
              {t('upgrade')}
            </Button>
          }
        />
        <InformationModal
          open={openStopConfirm}
          onClose={() => setOpenStopConfirm(false)}
          title={t('stopConfirm')}
          content={t('stopConfirmContent')}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleStopSubscription}
              loading={state.subscribing}
            >
              {t('stop')}
            </Button>
          }
        />
        <InformationModal
          open={openCancelChangePlanConfirm}
          onClose={() => setOpenCancelChangePlanConfirm(false)}
          title={t('cancelChangePlanConfirm')}
          content={t('cancelChangePlanConfirmContent')}
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleCancelChangePlan}
              loading={state.subscribing}
            >
              {t('cancelChangePlan')}
            </Button>
          }
        />
        <InformationModal
          open={openCarrierUpgradeConfirm}
          onClose={() => setOpenCarrierUpgradeConfirm(false)}
          title={t('confirm')}
          content={
            <Stack spacing={3} sx={{ pb: 7 }}>
              <Typography variant="body2">
                {t('carrierUpgradeConfirmContent')}
              </Typography>
              <Typography variant="body2">
                {t('carrierUpgradeConfirmExpired', {
                  expiredAt: DateTime.formatDate(state.expiredAtSubscription),
                })}
              </Typography>
            </Stack>
          }
          cancelText={t('cancel')}
          buttons={
            <Button
              size="large"
              sx={{ minWidth: 212 }}
              onClick={handleCarrierUpgradePlan}
              loading={state.subscribing}
            >
              {t('upgrade')}
            </Button>
          }
        />
        <InformationModal
          open={openKonbiniUpgradeConfirm}
          onClose={() => setOpenKonbiniUpgradeConfirm(false)}
          title={t('konbiniUpgradeConfirm')}
          content={
            <Stack spacing={3} pt={3}>
              <Typography variant="body2" align="left">
                {t('konbiniUpgradeConfirmContent')}
              </Typography>
              <Typography variant="body2" align="center">
                {t('nextUpdatedAt', { date: state.expiredAtSubscription })}
              </Typography>
            </Stack>
          }
          buttons={
            <Stack spacing={4} pt={7}>
              <Button
                size="large"
                sx={{ minWidth: 212 }}
                href={generatePath(routerPages.ArtistMembershipPaymentEdit, {
                  artistId: state.artist?.id ?? '#',
                  productId:
                    state.products?.find(product =>
                      product.plans.some(
                        plan => plan.planId === currentSelectPlan?.planId
                      )
                    )?.productId ?? '#',
                })}
              >
                {t('changePaymentMethod')}
              </Button>
              <Button
                size="large"
                variant="outlined"
                sx={{ minWidth: 212 }}
                onClick={() => setOpenKonbiniUpgradeConfirm(false)}
              >
                閉じる
                {/* NOTE: 更新時アップグレードのAPIがないので、現在は閉じるだけ */}
                {/* {t('upgradeOnRenewal')} */}
              </Button>
            </Stack>
          }
        />
        <ExpiredCardModal
          open={openExpiredCardModal}
          onClose={() => setOpenExpiredCardModal(false)}
        />

        <PaymentErrorModal
          open={openPaymentErrorModal}
          onClose={() => setOpenPaymentErrorModal(false)}
        />

        <Require3DSecureModal
          open={openRequire3DSecureModal}
          onClose={() => setOpenRequire3DSecureModal(false)}
        />
      </BaseLayout>
    </ArtistThemeProvider>
  )
}

type BannerCellProps = {
  artist: State['artist']
  onClose: () => void
  responsive: boolean
}

const BannerCell: Cell<BannerCellProps> = ({
  artist,
  onClose,
  children,
  responsive,
}) => {
  const [{ bgImage }] = useArtistThemeContext()

  return (
    <HeaderImageArea
      hasSafeArea
      gradationMask
      bgImage={bgImage}
      imageUri={artist?.avatar?.mediumUri}
      maxWidth={responsive ? 1120 : undefined}
      responsive={responsive}
    >
      <CellFrame
        sx={{
          width: '100%',
          height: { xs: 280, md: 584 },
        }}
        display="flex"
        flexDirection="column"
        justifyContent="space-between"
      >
        <AppBar
          position="static"
          sx={{
            bgcolor: 'transparent',
            backgroundImage: 'none',
            boxShadow: 'none',
            color: 'text.primary',
          }}
        >
          <Toolbar>
            <IconButton.Close onClick={onClose} bgcolor="grey.50" />
          </Toolbar>
        </AppBar>
      </CellFrame>
    </HeaderImageArea>
  )
}

type ArtistMemberLoadingCellProps = {
  onClose: () => void
}
const ArtistMemberLoadingCell: Cell<ArtistMemberLoadingCellProps> = ({
  onClose,
}) => (
  <CellFrame>
    <Skeleton
      variant="rectangular"
      width="100%"
      sx={{
        height: { xs: 280, md: 584 },
        '& header': { visibility: 'visible' },
      }}
    >
      <AppBar
        position="static"
        sx={{
          pt: styles.safeAreaTop,
          bgcolor: 'transparent',
          backgroundImage: 'none',
          boxShadow: 'none',
          color: 'text.primary',
        }}
      >
        <Toolbar>
          <IconButton.Close onClick={onClose} bgcolor="grey.50" />
        </Toolbar>
      </AppBar>
    </Skeleton>
    <Stack sx={{ px: 5, mt: '60px', pt: 5 }} spacing={5}>
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
      <Skeleton
        variant="rectangular"
        height={348}
        width="100%"
        sx={{ borderRadius: 2 }}
      />
    </Stack>
    <Stack sx={{ px: 5, py: 7 }}>
      <SkeletonText
        sx={{ py: 1 }}
        variant="small"
        numberOfLine={5}
        lastWidth="75%"
      />
    </Stack>
  </CellFrame>
)

type WebInductionCellProps = {
  appName: string
}
export const WebInductionCell: Cell<WebInductionCellProps> = ({ appName }) => {
  const { t } = useTranslation('artistMembership')
  return (
    <CellFrame sx={{ mt: 4, mx: 5, mb: -2 }}>
      <Stack
        sx={{
          borderRadius: 2,
          border: 1,
          borderColor: 'grey.100',
          pt: 5,
          px: 7,
          pb: 6,
        }}
        alignItems="center"
      >
        <Typography variant="body2">{t('cannotOpen')}</Typography>
        <Typography variant="subtitle1" color="text.hint" sx={{ mt: 4 }}>
          {t('searchBrowser')}
        </Typography>
        <Button
          endIcon={
            <Box>
              <Icons.Search fontSize="medium" />
            </Box>
          }
          sx={{ width: 275, mt: 2 }}
          disabled
        >
          <Typography variant="subtitle1" width="100%" sx={{ ml: 7 }}>
            {appName}
          </Typography>
        </Button>
      </Stack>
    </CellFrame>
  )
}
